import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Source } from '../icons/Icons'
import { ChatbotContext } from '../context/ChatbotProvider'
import {
  checkIsDrawerMode,
  fetchDomain,
  getFileDisplayName,
  getFileIcon,
  prepareCitations
} from '../utils/helpers'
import { IconWorld, IconLoader, IconX, IconDownload } from '@tabler/icons-react'
import { getClassForFileType, getFileType } from '../utils/TextUtils'
import { ChatBubbleItem, ChatBubbleResponse } from '../utils/types'
import {
  normalizeCitationFileName,
  normalizeDocCitation
} from '../shared/citation-preview/citationNormalizer'
import { useDocumentCitationPreview } from '../shared/citation-preview/useDocumentCitationPreview'
import { DocumentCitationPreviewPanel } from '../shared/citation-preview/DocumentCitationPreviewPanel'
import { getPreviewCategory } from '../shared/citation-preview/fileTypeRegistry'
import { ResolvedCitation } from '../shared/citation-preview/types'
import { useDataboxService } from '../../Teams/services/databoxService'
import { CitationPreviewText } from './CitationPreviewText'
import { appInsights } from '../../../../_services/AppInsights'
import { EventType } from '../../../../utility/types'

// getMyDataboxes (`/api/databox`) returns every databox the user can access - their personal one
// plus each team databox they belong to. A citation that carries no databox id of its own can
// still be resolved by searching them all, so collect every databox id and look up the document
// per databox exactly as the personal-databox path already does.
const extractAccessibleDataboxIds = (response: any): string[] => {
  const databoxes = Array.isArray(response) ? response : response?.items || []
  const ids = databoxes
    .map(
      (databox: any) =>
        databox?.id ||
        databox?.personalDataBoxId ||
        databox?.databoxId ||
        databox?.smartDocsDataboxId
    )
    .filter(Boolean)
    .map((databoxId: any) => String(databoxId))
  return Array.from(new Set(ids))
}

const sourceMatchesInlineCitation = (source: any, fileName?: string, pageNumber?: number) => {
  const sourceName = getFileDisplayName(source?.reference || source?.url || source?.name || '')
  const pages = [
    source?.page,
    source?.pageNumber,
    source?.metadata?.page,
    source?.metadata?.pageNumber
  ].flat()

  return (
    normalizeCitationFileName(sourceName) === normalizeCitationFileName(fileName) &&
    pages.some((page) => Number(page) === Number(pageNumber))
  )
}

const Citations: React.FC = () => {
  const [opening, setOpening] = useState(false)
  const [closing, setClosing] = useState(false)
  const {
    selectedMessage,
    setSelectedMessage,
    messages,
    rawMessages,
    threadPersonalDataBoxes,
    threadDataboxIds
  } = useContext(ChatbotContext)
  const message: ChatBubbleItem | undefined = messages.find(
    (msg: ChatBubbleItem) => msg.parentId === selectedMessage?.parentId
  )
  const requestedResponseIndex = selectedMessage?.inlineCitationResponseIndex
  const selectedResponseIndex =
    Number.isInteger(requestedResponseIndex) && message?.response?.[requestedResponseIndex]
      ? requestedResponseIndex
      : message?.activeResponseIndex
  const messageResponse: ChatBubbleResponse | undefined = message?.response[selectedResponseIndex]
  // Memoized so the `|| []` fallback doesn't produce a new array reference (and retrigger
  // downstream useMemo/useEffect dependents) on every render when there are no citations.
  const selectedSources: any = useMemo(() => messageResponse?.citations || [], [messageResponse])
  // Threads persisted before rawMessages were introduced retain uploads on grouped messages;
  // newer threads retain them on raw messages. Resolve citations from both representations.
  const uploadedResources: any[] = [...rawMessages, ...messages].flatMap(
    (chatMessage: any) => chatMessage.resources || []
  )
  // The backend can return every retrieved databox file as a citation even when the answer only
  // uses some of them. Treat a doc citation as "referenced" when its file name appears anywhere in
  // the response text - inline "[name, p. N]" markers, numbered headings, or prose - so the panel
  // hides the files the last answer never actually mentions.
  const normalizedResponseText = useMemo(
    () => (messageResponse?.content || '').toLowerCase().replace(/[^a-z0-9]+/g, ''),
    [messageResponse?.content]
  )

  // Re-filtering runs a substring search per citation against the full response text, so this is
  // memoized to skip the work on renders triggered by unrelated UI state (hover/download/preview).
  const visibleDocSources = useMemo(() => {
    const isReferencedDocCitation = (source: any): boolean =>
      [source?.name, source?.title, source?.reference, source?.url].some((candidate) => {
        const normalized = normalizeCitationFileName(candidate)
        // Guard against short/ambiguous names substring-matching unrelated words in the answer.
        return normalized.length >= 4 && normalizedResponseText.includes(normalized)
      })

    // Fall back to every citation when the answer names no files at all (e.g. short prose replies),
    // so nothing regresses for responses we can't scope; otherwise show only the referenced files.
    const referencedDocSources = selectedSources.filter(isReferencedDocCitation)
    return referencedDocSources.length > 0 ? referencedDocSources : selectedSources
  }, [selectedSources, normalizedResponseText])
  const { getDataboxDocumentsPaginated, getMyDataboxes, getDocumentBlobUrl } = useDataboxService()
  const isDrawerMode = checkIsDrawerMode()
  const [validatedSources, setValidatedSources] = useState<any[]>([])
  const documentPreview = useDocumentCitationPreview()
  const userId = useSelector((state: any) => state?.profileReducer?.profile?.email) || 'unknown'
  // Gives the clicked download button immediate visual feedback while the file downloads.
  const [downloadingCitationKey, setDownloadingCitationKey] = useState<string | null>(null)
  // Gives the clicked row/page-link immediate visual feedback while the citation's real
  // document id is being resolved, so a slow (or ultimately failing) lookup never looks like
  // the click did nothing.
  const [openingCitationKey, setOpeningCitationKey] = useState<string | null>(null)
  const [openingFileName, setOpeningFileName] = useState<string | undefined>()
  const consumedInlineCitationRef = useRef<string | null>(null)
  const openRequestRef = useRef(0)
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [completedInlineCitationKey, setCompletedInlineCitationKey] = useState<string | null>(null)

  const inlineCitationIndex = selectedMessage?.inlineCitationIndex
  const inlineCitationSource =
    selectedSources.find((candidate: any) =>
      sourceMatchesInlineCitation(
        candidate,
        selectedMessage?.inlineCitationFileName,
        selectedMessage?.inlineCitationPageNumber
      )
    ) || selectedSources[inlineCitationIndex]
  const hasInlinePreviewIntent =
    Number.isInteger(inlineCitationIndex) &&
    inlineCitationIndex >= 0 &&
    Boolean(inlineCitationSource)
  const inlineCitationRequestKey = hasInlinePreviewIntent
    ? `${selectedMessage?.parentId}-${selectedResponseIndex}-${inlineCitationIndex}-${
        selectedMessage?.inlineCitationFileName || ''
      }-${selectedMessage?.inlineCitationPageNumber || ''}-${
        selectedMessage?.inlineCitationRequestId || 'legacy'
      }`
    : null
  const isInlinePreviewPending = Boolean(
    inlineCitationRequestKey && completedInlineCitationKey !== inlineCitationRequestKey
  )

  // Stable key to avoid re-running effects on every render (citations array is a new ref each time)
  const citationsKey = `${selectedMessage?.parentId}-${selectedResponseIndex}-${selectedSources.length}`
  // Tracks whether a document preview has been opened for the current citation list, so the list
  // only plays its "back" slide-in animation when returning from a drilldown - not on first open.
  const [hasEnteredPreview, setHasEnteredPreview] = useState(false)

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setOpening(true)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [citationsKey])

  useEffect(() => {
    setHasEnteredPreview(false)
  }, [citationsKey])

  useEffect(() => {
    if (documentPreview.previewOpen) setHasEnteredPreview(true)
  }, [documentPreview.previewOpen])

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    },
    []
  )

  const handleClose = () => {
    setClosing(true)
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    closeTimeoutRef.current = setTimeout(() => {
      setSelectedMessage(null)
      closeTimeoutRef.current = null
    }, 300) // Match this duration with your CSS transition duration
  }

  const handlePreviewBack = () => {
    openRequestRef.current += 1
    documentPreview.closePreview()
    consumedInlineCitationRef.current = null
    setCompletedInlineCitationKey(null)
    setSelectedMessage((currentSelection: any) => {
      if (!currentSelection) return currentSelection

      const {
        inlineCitationIndex: _inlineCitationIndex,
        inlineCitationPageNumber: _inlineCitationPageNumber,
        inlineCitationFileName: _inlineCitationFileName,
        inlineCitationResponseIndex: _inlineCitationResponseIndex,
        inlineCitationTrigger: _inlineCitationTrigger,
        inlineCitationRequestId: _inlineCitationRequestId,
        ...citationSelection
      } = currentSelection
      return citationSelection
    })
  }

  const openDocumentPreview = async (
    source: any,
    triggerElement: HTMLElement | null,
    openingKey: string,
    targetPageNumber?: number
  ) => {
    const requestId = ++openRequestRef.current
    setOpeningCitationKey(openingKey)
    let citation: ResolvedCitation | null = null

    try {
      citation = normalizeDocCitation(source, uploadedResources)
      const needsDocumentRendition =
        getPreviewCategory(citation?.title || '') === 'document' && !citation?.renditionId

      if (!citation?.fileId || needsDocumentRendition) {
        const databoxIds = Array.from(
          new Set(
            [
              source.databoxId,
              source.smartDocsDataboxId,
              source.teamId,
              ...threadDataboxIds,
              ...(threadPersonalDataBoxes || []).map((databox: any) => databox.personalDataBoxId)
            ].filter(Boolean)
          )
        )

        if (databoxIds.length === 0) {
          try {
            const response = await getMyDataboxes()
            if (requestId !== openRequestRef.current) return false
            extractAccessibleDataboxIds(response).forEach((databoxId) => databoxIds.push(databoxId))
          } catch (error) {
            console.error('Failed to resolve databoxes for citation preview:', error)
          }
        }

        for (const databoxId of databoxIds) {
          try {
            const response = await getDataboxDocumentsPaginated(
              String(databoxId),
              1,
              100,
              undefined,
              'uploadTime',
              'desc'
            )
            if (requestId !== openRequestRef.current) return false
            const documents = Array.isArray(response) ? response : response?.items || []
            citation = normalizeDocCitation(source, [...documents, ...uploadedResources])
            const hasRequiredRendition =
              getPreviewCategory(citation?.title || '') !== 'document' ||
              Boolean(citation?.renditionId)
            if (citation?.fileId && hasRequiredRendition) break
          } catch (error) {
            console.error('Failed to resolve citation document from databox:', error)
          }
        }
      }
    } catch (error) {
      // An unexpected shape anywhere above must not leave the click looking like it did
      // nothing — fall through and still open the panel below, using whatever citation (or
      // fallback) is available so the hook's own error state explains the failure.
      console.error('Failed to resolve citation document metadata:', error)
    }

    if (requestId !== openRequestRef.current) return false

    // normalizeDocCitation only returns null when `source` itself is falsy, but guard anyway:
    // opening the panel with a bare-minimum citation still surfaces a clear error to the user
    // instead of a click that silently does nothing.
    const resolvedCitation: ResolvedCitation = citation || {
      citationId: source?.citationId || source?.reference || source?.url || 'unknown-citation',
      fileId: '',
      title:
        source?.name || source?.title || source?.reference || source?.url || 'Untitled document',
      databoxId: '',
      locations: []
    }

    // openingKey encodes how the preview was triggered (row-*, page-*, inline-*, file-ref-*) so
    // analytics can distinguish a file-row open from a specific page-number/inline/file-ref open.
    const openTrigger = openingKey.startsWith('page-')
      ? 'page_number'
      : openingKey.startsWith('inline-')
      ? 'inline_marker'
      : openingKey.startsWith('file-ref-')
      ? 'file_reference'
      : 'citation_row'

    try {
      await documentPreview.openPreview(
        resolvedCitation,
        triggerElement,
        targetPageNumber,
        openTrigger
      )
      return requestId === openRequestRef.current
    } finally {
      if (requestId === openRequestRef.current) setOpeningCitationKey(null)
    }
  }

  // Resolves just the databox fileId for a citation (no rendition lookup needed — downloads
  // always fetch the original file bytes), so the download button doesn't have to open the
  // preview panel first.
  const resolveCitationFileId = async (source: any): Promise<ResolvedCitation | null> => {
    let citation: ResolvedCitation | null = normalizeDocCitation(source, uploadedResources)
    if (citation?.fileId) return citation

    const databoxIds = Array.from(
      new Set(
        [
          source.databoxId,
          source.smartDocsDataboxId,
          source.teamId,
          ...threadDataboxIds,
          ...(threadPersonalDataBoxes || []).map((databox: any) => databox.personalDataBoxId)
        ].filter(Boolean)
      )
    )

    if (databoxIds.length === 0) {
      try {
        const response = await getMyDataboxes()
        extractAccessibleDataboxIds(response).forEach((databoxId) => databoxIds.push(databoxId))
      } catch (error) {
        console.error('Failed to resolve databoxes for citation download:', error)
      }
    }

    for (const databoxId of databoxIds) {
      try {
        const response = await getDataboxDocumentsPaginated(
          String(databoxId),
          1,
          100,
          undefined,
          'uploadTime',
          'desc'
        )
        const documents = Array.isArray(response) ? response : response?.items || []
        citation = normalizeDocCitation(source, [...documents, ...uploadedResources])
        if (citation?.fileId) break
      } catch (error) {
        console.error('Failed to resolve citation document for download:', error)
      }
    }

    return citation
  }

  const handleDownloadClick = async (source: any, downloadKey: string, event: React.MouseEvent) => {
    event.stopPropagation()
    setDownloadingCitationKey(downloadKey)
    try {
      const citation = await resolveCitationFileId(source)
      appInsights?.trackEvent({
        name: EventType.CitationPreviewInteraction,
        properties: {
          userId,
          actionType: 'citation_download',
          fileName: citation?.title,
          timestamp: new Date().toISOString()
        }
      })
      if (!citation?.fileId) return
      await getDocumentBlobUrl(citation.fileId, getFileDisplayName(citation.title))
    } catch (error) {
      console.error('Failed to download citation document:', error)
    } finally {
      setDownloadingCitationKey(null)
    }
  }

  useEffect(() => {
    const citationIndex = selectedMessage?.inlineCitationIndex
    if (!Number.isInteger(citationIndex) || citationIndex < 0) return

    const source = inlineCitationSource
    if (!source) return

    const requestKey = inlineCitationRequestKey
    if (!requestKey) return
    if (consumedInlineCitationRef.current === requestKey) return
    consumedInlineCitationRef.current = requestKey

    void openDocumentPreview(
      source,
      selectedMessage.inlineCitationTrigger || null,
      `inline-${citationIndex}`,
      selectedMessage.inlineCitationPageNumber
    ).then((openedLatestRequest) => {
      if (openedLatestRequest) setCompletedInlineCitationKey(requestKey)
    })
  }, [selectedMessage, citationsKey, inlineCitationRequestKey])

  /**
   * Handler for clicking file references in citation preview text
   * Searches for the file in uploaded resources and opens its preview
   * Logs telemetry event for the interaction
   */
  const handleFileReferenceClick = async (fileName: string, triggerElement: HTMLButtonElement) => {
    setOpeningFileName(fileName)

    try {
      // Search for matching file in uploaded resources
      const normalizedFileName = normalizeCitationFileName(fileName)
      const matchingResource = uploadedResources.find((resource: any) =>
        [resource?.name, resource?.originalFileName, resource?.fileName].some(
          (candidate) => normalizeCitationFileName(candidate) === normalizedFileName
        )
      )

      if (!matchingResource) {
        // File not found in uploaded resources - log error telemetry
        appInsights?.trackEvent({
          name: EventType.CitationPreviewError,
          properties: {
            errorType: 'file_reference_not_found',
            fileName,
            userId,
            timestamp: new Date().toISOString()
          }
        })
        return
      }

      // Create a source object from the uploaded resource for normalization
      const sourceObject = {
        reference: matchingResource.resourceId || matchingResource.id,
        url: matchingResource.blobUrl || matchingResource.url,
        name: fileName,
        title: fileName,
        type: 'doc',
        databoxId: matchingResource.databoxId || ''
      }

      // Log telemetry for file reference click
      appInsights?.trackEvent({
        name: EventType.CitationPreviewInteraction,
        properties: {
          actionType: 'file_reference_preview',
          fileName,
          userId,
          timestamp: new Date().toISOString()
        }
      })

      // Open the preview for this file
      await openDocumentPreview(sourceObject, triggerElement, `file-ref-${fileName}`)
    } catch (error) {
      // Log error telemetry
      appInsights?.trackEvent({
        name: EventType.CitationPreviewError,
        properties: {
          errorType: 'file_reference_preview_error',
          fileName,
          userId,
          error: String(error),
          timestamp: new Date().toISOString()
        }
      })
      console.error('Failed to open file reference preview:', error)
    } finally {
      setOpeningFileName(undefined)
    }
  }

  useEffect(() => {
    if (
      selectedSources &&
      selectedSources.length > 0 &&
      selectedSources[0]?.type?.toLowerCase() === 'web'
    ) {
      // Guard against a slow favicon resolve for a previous selection overwriting a newer one
      // (and against setting state after the panel has unmounted).
      let isCurrent = true
      prepareCitations(selectedSources).then((enrichedSources) => {
        if (isCurrent) setValidatedSources(enrichedSources)
      })
      return () => {
        isCurrent = false
      }
    }
    setValidatedSources([])
  }, [citationsKey])

  return (
    <div
      className={`citations theme-surface border-l border-divider shadow-overlay transition-all duration-300 ease-in-out transform z-[15] h-full mb-1 top-0
        w-[528px] max-w-[80vw]
        min-w-0
        ${selectedSources ? 'mr-0' : '-mr-[400px]'} 
        ${isDrawerMode ? 'absolute' : 'relative'} 
        ${selectedSources ? 'right-0' : '-right-[400px]'}
        ${opening ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'} 
        ${closing ? 'translate-x-full opacity-0' : ''}`}
      data-testid="citations-container">
      {isInlinePreviewPending ? (
        <div
          className="flex h-full items-center justify-center"
          role="status"
          aria-live="polite"
          data-testid="inline-citation-preview-loading">
          <IconLoader size={28} className="theme-icon-muted animate-spin" aria-hidden="true" />
          <span className="sr-only">Loading document preview</span>
        </div>
      ) : documentPreview.previewOpen ? (
        <div className="citation-drilldown-forward-enter h-full">
          <DocumentCitationPreviewPanel
            preview={documentPreview}
            onBack={handlePreviewBack}
            onClose={handleClose}
          />
        </div>
      ) : (
        <div className={hasEnteredPreview ? 'citation-drilldown-back-enter h-full' : 'h-full'}>
          <div
            className={`rounded-tl-lg w-full flex justify-between items-center p-1 text-white bg-primary`}>
            <div className="flex gap-50 items-center">
              <Source color="var(--white)" size={26} />
              <span className="font-semibold">Citations</span>
            </div>
            <IconX
              data-testid="close-citations"
              className="cursor-pointer"
              color="var(--chat-theme-primary-light)"
              onClick={handleClose}
            />
          </div>
          <div
            className={`${
              selectedSources ? 'flex' : 'hidden'
            } flex-col overflow-y-auto h-[calc(100%-50px)] p-50 scrollbar-none`}>
            {selectedSources?.length ? (
              selectedSources[0]?.type?.toLowerCase() === 'web' ? (
                validatedSources.map((source: any, index: number) => {
                  const icon = source?.icon
                  const name = source?.name
                    ? source.name
                    : fetchDomain(source?.reference || source?.url)
                  const title = source?.title || ''
                  const preview = source?.preview || ''
                  const url = source?.reference || source?.url
                  return (
                    <div
                      key={index}
                      className="cursor-pointer rounded-lg transition-colors hover:bg-theme-hover p-1 mb-50"
                      onClick={() => window.open(url, '_blank')}>
                      <span className="flex items-start  ">
                        {icon ? (
                          <img
                            src={icon}
                            alt={name}
                            width={24}
                            className="me-50 rounded-full flex-shrink-0 max-h-[30px] border border-[var(--theme-border)]"
                          />
                        ) : (
                          <IconWorld size={20} color="var(--color-primary)" />
                        )}
                        <span className="flex-1 ml-1 theme-text-body">{name}</span>
                      </span>
                      <h5 className="flex-1 font-bold theme-text-subheading mt-50">{title}</h5>
                      {preview && (
                        <p className="theme-text-subheading leading-6 line-clamp-2 overflow-hidden text-ellipsis mb-0">
                          {preview}
                        </p>
                      )}
                    </div>
                  )
                })
              ) : selectedSources[0]?.type?.toLowerCase() === 'doc' ? (
                Object.values(
                  visibleDocSources.reduce((acc: any, source: any) => {
                    const key = `${source.type}-${source.reference || source.url}`
                    if (!acc[key]) {
                      acc[key] = { ...source, locations: [source] }
                    } else {
                      acc[key].locations.push(source)
                    }
                    return acc
                  }, {})
                )
                  .sort(
                    (firstSource: any, secondSource: any) =>
                      Number(firstSource.locations[0]?.page) -
                      Number(secondSource.locations[0]?.page)
                  )
                  .map((source: any, index: number) => {
                    const fileName = getFileDisplayName(source.reference || source.url || '')
                    const locations = [...source.locations].sort(
                      (firstLocation: any, secondLocation: any) =>
                        Number(firstLocation.page) - Number(secondLocation.page)
                    )
                    const fileType = getFileType(fileName)

                    const rowKey = `row-${index}`

                    return (
                      <div key={index} className="flex flex-col mb-1 p-1 w-100 text-start">
                        <div className="flex w-full items-center gap-50">
                          <button
                            type="button"
                            className="flex flex-1 min-w-0 items-center gap-50 text-start bg-transparent border-0 p-0 cursor-pointer overflow-hidden disabled:cursor-wait disabled:opacity-70"
                            title={getFileDisplayName(fileName)}
                            aria-label={`Open preview for ${getFileDisplayName(fileName)}`}
                            aria-busy={openingCitationKey === rowKey}
                            disabled={openingCitationKey !== null}
                            onClick={(event) =>
                              openDocumentPreview(source, event.currentTarget, rowKey)
                            }>
                            <div
                              className={`w-[60px] h-[40px] rounded-none flex items-center justify-center flex-shrink-0 ${getClassForFileType(
                                fileType
                              )}`}>
                              {openingCitationKey === rowKey ? (
                                <IconLoader size={18} className="animate-spin" aria-hidden="true" />
                              ) : (
                                getFileIcon(fileName)
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <h5 className="theme-text-heading text-body-small mb-0 truncate leading-5">
                                <b className="theme-text-heading">{getFileDisplayName(fileName)}</b>
                              </h5>
                            </div>
                          </button>
                          <button
                            type="button"
                            className="theme-icon-button flex-shrink-0 flex items-center justify-center rounded-md p-50 bg-transparent border-0 cursor-pointer transition-colors disabled:cursor-wait disabled:opacity-70"
                            title={`Download ${getFileDisplayName(fileName)}`}
                            aria-label={`Download ${getFileDisplayName(fileName)}`}
                            aria-busy={downloadingCitationKey === rowKey}
                            disabled={downloadingCitationKey !== null}
                            onClick={(event) => handleDownloadClick(source, rowKey, event)}>
                            {downloadingCitationKey === rowKey ? (
                              <IconLoader size={18} className="animate-spin" aria-hidden="true" />
                            ) : (
                              <IconDownload size={18} aria-hidden="true" />
                            )}
                          </button>
                        </div>
                        {locations.length > 0 && (
                          <div className="mt-1 flex flex-col">
                            {locations.map((location: any, locationIndex: number) => {
                              const pageKey = `page-${index}-${locationIndex}`
                              return (
                                <div
                                  key={`${location.page}-${locationIndex}`}
                                  className="flex flex-col gap-25 py-50 first:pt-0">
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      className="text-primary text-body-small bg-transparent border-0 p-0 m-0 font-inherit cursor-pointer underline disabled:cursor-wait disabled:no-underline disabled:opacity-70"
                                      aria-label={`Preview ${getFileDisplayName(fileName)}, page ${
                                        location.page
                                      }`}
                                      aria-busy={openingCitationKey === pageKey}
                                      disabled={openingCitationKey !== null}
                                      onClick={(event) =>
                                        openDocumentPreview(
                                          source,
                                          event.currentTarget,
                                          pageKey,
                                          location.page
                                        )
                                      }>
                                      {openingCitationKey === pageKey ? (
                                        <IconLoader
                                          size={12}
                                          className="inline-block animate-spin align-[-1px] mr-25"
                                          aria-hidden="true"
                                        />
                                      ) : null}
                                      Page {location.page}
                                    </button>
                                  </div>
                                  {location.preview && (
                                    <CitationPreviewText
                                      text={location.preview}
                                      onFileClick={(fileName, event) =>
                                        handleFileReferenceClick(fileName, event.currentTarget)
                                      }
                                      isLoading={openingFileName !== null}
                                      openingFileName={openingFileName}
                                    />
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })
              ) : null
            ) : (
              <div className="d-flex h-100 align-items-center text-center px-50">
                <h4 className="theme-text-subheading">
                  Oops! No citations are available for this response.
                </h4>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Citations
